import React, { type ReactNode, type MouseEvent, useContext, useEffect, useState, type AriaAttributes } from 'react';

import { useSmartlingStrings } from '@lucid/hooks';
import { navigate } from 'gatsby';

import Box from '../box/box';
import Icon from '../icon/icon';
import Link from '../link/link';
import LayoutContext from '../context/layout-context';
import renderComponent, { type RenderComponent } from '../../utils/render-component';

export type PaginationRenderProps<T> = {
  /** The current activePage number. */
  activePage: number;
  /** The number of items to be returned per each pagination page. `@default = 5` */
  itemsPerPage?: number;
  /** The totalPages that have been paginated. */
  pageCount: number;
  /** The visible items that get returned. */
  visibleItems: T[];
};

type CallbackHandler = {
  /** The current activePage number. */
  activePage: number;
  /** The totalPages that have been paginated. */
  pageCount: number;
};

export type PaginationProps<T> = {
  /** The number of visible items before and after the ellipsis to always show. `@default = 1` */
  alwaysVisiblePageCount?: number;
  /** The children that are returned with the `Pagination` component. */
  children: RenderComponent<PaginationRenderProps<T>>;
  /** Allows the component to get additional styles. */
  className?: string;
  /** Callback to set current ActivePage & current TotalPages. */
  handleCallback?: (values: CallbackHandler) => void;
  /** The list of items to return with pagination. */
  items: T[];
  /** The number of items to be returned per each pagination page. `@default = 5` */
  itemsPerPage?: number;
  /** The onClick handler callback. */
  onClick?: (value: number) => void;
  /** Will render the pagination buttons as Link components using the returned string as the `to` prop */
  pageLink?: (value: number) => string;
  /** Checks whether or not the items should always be visible. */
  shouldAlwaysBeVisible?: boolean;
  /** Returns all items not paginated. */
  shouldNotPaginate?: boolean;
  /** Changes the page to start from for the pagination data. */
  startPage?: number;
  /** The total number of available pages (if not to be infered from the number of items) */
  totalPages?: number;
  /** The number of items away from the start/finish before showing ellipsis. `@default = 5` */
  visiblePageCount?: number;
  /** The number of items to show after the ellipsis. `@default = 1` */
  visibleEndPageCount?: number;
} & AriaAttributes;

const Pagination = <T,>({
  alwaysVisiblePageCount = 1,
  children,
  className,
  handleCallback,
  startPage = 1,
  items,
  itemsPerPage = 5,
  onClick,
  pageLink,
  shouldAlwaysBeVisible = false,
  shouldNotPaginate = false,
  totalPages,
  visiblePageCount = 5,
  visibleEndPageCount = 1,
  ...rest
}: PaginationProps<T>) => {
  const ariaLabel = rest['aria-label'] ?? 'pagination';
  const { shortCode } = useContext(LayoutContext);
  const withSmartling = useSmartlingStrings({
    keys: [
      'mktg_blog_aria_label_pagination_next',
      'mktg_blog_aria_label_pagination_previous',
      'mktg_blog_aria_label_pagination_page',
    ],
    shortCode,
  });
  const [activePage, setActivePage] = useState(startPage);
  const pageCount = totalPages ? totalPages : shouldNotPaginate ? 1 : Math.ceil(items.length / itemsPerPage);
  const willShowPrev = activePage > 1;
  const willShowNext = activePage < pageCount;
  const visibleItemStart = (activePage - 1) * itemsPerPage;
  const visibleItemEnd = activePage * itemsPerPage;
  const visibleItems = totalPages ? items : shouldNotPaginate ? items : items.slice(visibleItemStart, visibleItemEnd);
  const isSequential = (vals: number[]) => vals[1] === vals[0] + 1;
  const renderedChildren = renderComponent({
    children,
    activePage,
    itemsPerPage,
    pageCount,
    visibleItems,
  });
  let preChildren: ReactNode;
  let postChildren: ReactNode;

  useEffect(() => {
    setActivePage(startPage);
  }, [startPage, setActivePage]);

  if (shouldAlwaysBeVisible) {
    const preInvisibleItems = items.slice(0, visibleItemStart);

    if (preInvisibleItems.length > 0) {
      preChildren = renderComponent({
        children,
        activePage,
        itemsPerPage,
        pageCount,
        visibleItems: preInvisibleItems,
      });
    }

    const postInvisibleItems = items.slice(visibleItemEnd);

    if (postInvisibleItems.length > 0) {
      postChildren = renderComponent({
        children,
        activePage,
        itemsPerPage,
        pageCount,
        visibleItems: postInvisibleItems,
      });
    }
  }

  const currentVisiblePages = [...Array(pageCount).keys()]
    .map(i => i + 1)
    .filter(currentPage => {
      const isWithinStartTail = currentPage <= visibleEndPageCount;
      const isWithinEndTail = currentPage >= pageCount - visibleEndPageCount + 1;
      const isWithinNeighborDistance = Math.abs(currentPage - activePage) <= alwaysVisiblePageCount;
      const isWithinFirstEllipsisDistance =
        activePage <= visiblePageCount && currentPage <= visiblePageCount + alwaysVisiblePageCount;
      const isWithinLastEllipsisDistance =
        activePage > pageCount - visiblePageCount &&
        currentPage > pageCount - visiblePageCount - alwaysVisiblePageCount;

      return (
        isWithinStartTail ||
        isWithinEndTail ||
        isWithinNeighborDistance ||
        isWithinFirstEllipsisDistance ||
        isWithinLastEllipsisDistance
      );
    });

  if (currentVisiblePages.length >= visiblePageCount) {
    const firstTwoItems = currentVisiblePages.slice(visibleEndPageCount - 1, visibleEndPageCount + 1);
    const lastTwoItems = currentVisiblePages.slice(
      -(visibleEndPageCount + 1),
      currentVisiblePages.length - visibleEndPageCount + 1,
    );

    if (!isSequential(firstTwoItems)) {
      const startEllipsisId = -1;
      currentVisiblePages.splice(visibleEndPageCount, 0, startEllipsisId);
    }

    if (!isSequential(lastTwoItems)) {
      const endEllipsisId = -2;
      currentVisiblePages.splice(currentVisiblePages.length - visibleEndPageCount, 0, endEllipsisId);
    }
  }

  useEffect(() => {
    if (handleCallback && startPage !== activePage) {
      handleCallback({
        activePage,
        pageCount,
      });
    }
  }, [activePage, handleCallback, pageCount, startPage]);

  const handleOnClick = (e: MouseEvent<HTMLAnchorElement>, value: number) => {
    e.preventDefault();

    if (onClick) {
      onClick(value);
    }
    setActivePage(value);

    if (pageLink && !onClick) {
      navigate(pageLink(value));
    }
  };

  return (
    <>
      {preChildren && <Box css={{ display: 'none' }}>{preChildren}</Box>}
      <Box
        css={{
          label: 'paginationChildren',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
        }}
      >
        {renderedChildren}
      </Box>
      {postChildren && <Box css={{ display: 'none' }}>{postChildren}</Box>}
      <nav
        {...rest}
        css={theme => ({
          label: 'pagination',
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'column',
          paddingTop: theme.space.s32,
          paddingBottom: theme.space.s40,
        })}
        // eslint-disable-next-line @stylistic/jsx-sort-props
        className={className}
        data-testid="paginationNavigationContainer"
      >
        {!shouldNotPaginate && pageCount > 1 && items.length > 0 && (
          <ul
            key="paginationUL"
            css={{
              margin: 0,
              label: 'paginationNavigation',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              listStyle: 'none',
            }}
          >
            {willShowPrev && (
              <li>
                <Link
                  aria-label={`${withSmartling('mktg_blog_aria_label_pagination_previous', { page: `${activePage - 1}` }) || 'Go to previous page'}${ariaLabel ? `, ${ariaLabel}` : ''}`}
                  css={theme => ({
                    color: theme.colors.coolGrey[50],
                    verticalAlign: 'middle',
                    outline: 'none',
                    '&:hover, &:focus': {
                      color: theme.colors.coolGrey[100],
                    },
                  })}
                  data-testid="paginationPreviousPage"
                  href={pageLink ? pageLink(activePage - 1) : undefined}
                  onClick={e => {
                    handleOnClick(e, activePage - 1);
                  }}
                  rel="prev"
                  role={pageLink ? undefined : 'button'}
                  tabIndex={0}
                >
                  <Icon
                    css={{
                      fontSize: '1rem',
                      transform: 'rotate(180deg)',
                      marginRight: '1rem',
                    }}
                    type="CarotRight"
                  />
                </Link>
              </li>
            )}
            {currentVisiblePages.map(currentPage =>
              currentPage > 0 ? (
                <li key={`paginationCurrentPage-${currentPage}`}>
                  <Link
                    key={currentPage}
                    displayAsButton
                    aria-current={activePage === currentPage}
                    aria-label={`${withSmartling('mktg_blog_aria_label_pagination_page', { page: `${currentPage}` }) || `Go to page ${currentPage}`}${ariaLabel ? `, ${ariaLabel}` : ''}`}
                    css={theme => [
                      {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: theme.space.s4,
                        width: '2.5rem',
                        height: '2.5rem',
                        fontSize: theme.fontSizes.fs16,
                        color: theme.colors.coolGrey[80],
                        borderRadius: theme.radii.r2,
                        border: `1px solid ${theme.colors.coolGrey[80]}`,
                        boxShadow: 'none',
                        margin: theme.space.s4,
                        outline: 'none',
                        backgroundColor: theme.colors.white,
                        [theme.breakpoints.bp768.min]: {
                          margin: theme.space.s8,
                        },
                        '&:hover, &:focus': {
                          color: theme.colors.coolGrey[100],
                          backgroundColor: theme.colors.white,
                          border: `1px solid ${theme.colors.coolGrey[100]}`,
                        },
                      },
                      activePage === currentPage && {
                        '&, &:hover, &:focus': {
                          backgroundColor: theme.colors.coolGrey[100],
                          color: theme.colors.white,
                          border: `1px solid ${theme.colors.coolGrey[100]}`,
                        },
                      },
                    ]}
                    data-testid={`paginationCurrentPage-${currentPage}`}
                    href={pageLink ? pageLink(currentPage) : undefined}
                    isActive={activePage === currentPage}
                    onClick={e => {
                      handleOnClick(e, currentPage);
                    }}
                    role={pageLink ? undefined : 'button'}
                    tabIndex={0}
                    variant={activePage === currentPage ? 'primary' : 'secondary'}
                  >
                    {currentPage}
                  </Link>
                </li>
              ) : (
                <li key={`paginationEllipsis-${currentPage}`}>
                  <span
                    key={currentPage}
                    css={theme => ({
                      display: 'inline-block',
                      textAlign: 'center',
                      margin: theme.space.s8,
                      [theme.breakpoints.bp768.min]: {
                        width: '2.5rem',
                      },
                    })}
                  >
                    ...
                  </span>
                </li>
              ),
            )}
            {willShowNext ? (
              <li>
                <Link
                  aria-label={`${withSmartling('mktg_blog_aria_label_pagination_next', { page: `${activePage + 1}` }) || 'Go to next page'}${ariaLabel ? `, ${ariaLabel}` : ''}`}
                  css={theme => ({
                    color: theme.colors.coolGrey[50],
                    verticalAlign: 'middle',
                    outline: 'none',
                    '&:hover, &:focus': {
                      color: theme.colors.coolGrey[100],
                    },
                  })}
                  data-testid="paginationNextPage"
                  href={pageLink ? pageLink(activePage + 1) : undefined}
                  onClick={e => {
                    handleOnClick(e, activePage + 1);
                  }}
                  rel="next"
                  role={pageLink ? undefined : 'button'}
                  tabIndex={0}
                >
                  <Icon
                    css={theme => ({
                      fontSize: theme.fontSizes.fs16,
                      marginLeft: theme.space.s16,
                    })}
                    type="CarotRight"
                  />
                </Link>
              </li>
            ) : (
              <li aria-hidden="true" css={{ marginLeft: '2rem' }}></li>
            )}
          </ul>
        )}
      </nav>
    </>
  );
};

export { Pagination };
export default Pagination;
